import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  FileText, 
  Users, 
  Download, 
  Calendar, 
  Eye, 
  Link as LinkIcon,
  Hash,
  Mail,
  Home,
  ChevronRight,
  BookOpen,
  AlertCircle,
  Loader,
  X
} from 'lucide-react';
import RichTextViewer from './common/RichTextViewer';
import axios from "../utils/axios"
import PageHeader from './PageHeader';

const PreviewIssue = () => {
  // Mock data based on your HTML
    const [searchParams] = useSearchParams();
  const buffer = searchParams.get('sid');
  
  const [manuscript, setManuscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
   const [downloading, setDownloading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [citationCount, setCitationCount] = useState(0);
  const [citationLoading, setCitationLoading] = useState(false);
  const [citationList, setCitationList] = useState([]);
  const [showCitationList, setShowCitationList] = useState(false);

const handleDownload = async (e) => {
    e.preventDefault(); // Prevent default anchor behavior
    
    try {
        // First, update download count on the server
        const response = await axios.get('/journal/public/update-download-count', {
            params: { buffer }
        });

        if (response.data.success) {
            console.log("Download count updated successfully");
            
            // After updating the count, trigger the file download
            // Create a temporary anchor element
            const link = document.createElement('a');
            link.href = manuscript.pdfUrl;
            link.download = `${manuscript.title.replace(/[^a-z0-9]/gi, '_')}_ASFIRJ.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } else {
            console.error(response.data.error || "Failed to update download count");
            // Still allow download even if count update fails
            // window.open(manuscript.pdfUrl, '_blank');
                const link = document.createElement('a');
            link.href = manuscript.pdfUrl;
            link.download = `${manuscript.title.replace(/[^a-z0-9]/gi, '_')}_ASFIRJ.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        
    } catch (err) {
        console.error("Error updating download count:", err);
        // Fallback: still allow download even if server update fails
        window.open(manuscript.pdfUrl, '_blank');
    }
};

const switchLanguage = (lang) => {
  setCurrentLanguage(lang);
};

// Get translated abstract based on selected language
const getTranslatedAbstract = () => {
  switch(currentLanguage) {
    case 'fr':
      return manuscript.abstract_fr || "No abstract available in French. Please refer to the English version.";
    case 'ar':
      return manuscript.abstract_ar ||"No abstract available in Arabic. Please refer to the English version.";
    case 'pt':
      return manuscript.abstract_ptg || "No abstract available in Portuguese. Please refer to the English version.";
    default:
      return manuscript.abstract;
  }
};

   useEffect(() => {
    const fetchArticleData = async () => {
      if (!buffer) {
        setError("No article identifier provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get('/journal/public/article-data', {
          params: { buffer }
        });

        if (response.data.success) {
          setManuscript(response.data.article);
        } else {
          setError(response.data.error || "Failed to load article");
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        if (err.response) {
          // Server responded with error status
          setError(err.response.data.error || `Server error: ${err.response.status}`);
        } else if (err.request) {
          // Request was made but no response
          setError("Network error. Please check your connection.");
        } else {
          // Something else happened
          setError("An unexpected error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArticleData();
  }, [buffer]);

  // Fetch citation count when manuscript loads
  useEffect(() => {
    if (!manuscript?.doi) {
      setCitationCount(0);
      setCitationList([]);
      return;
    }
    const fetchCitations = async () => {
      setCitationLoading(true);
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:31000';
      try {
        const response = await fetch(`${baseUrl}/journal/public/fetch-citations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ doi_number: manuscript.doi })
        });
        const data = await response.json();
        if (data.success && data.data) {
          setCitationCount(data.data.total_citations || 0);
          setCitationList(data.data.citations || []);
        }
      } catch {
        setCitationCount(0);
        setCitationList([]);
      } finally {
        setCitationLoading(false);
      }
    };
    fetchCitations();
  }, [manuscript?.doi]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-[#f8f9fa] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-[#80078b] mx-auto mb-4" />
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-[#f8f9fa] min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Unable to Load Article</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-[#80078b] text-white rounded-lg hover:bg-[#6a0673] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!manuscript) {
    return (
      <div className="bg-[#f8f9fa] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Article not found</p>
        </div>
      </div>
    );
  }


  return (
    <div className="bg-[#f8f9fa] min-h-screen flex flex-col font-sans">
      <PageHeader 
        title="Preview Issue"
        subtitle="Preview Issue"
      />

      {/* 2. Breadcrumbs */}
      {/* <div className="bg-gray-100 py-3 border-b">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-2 text-sm text-gray-600">
          <Home size={14} />
          <a href="/" className="hover:underline">Home</a>
          <ChevronRight size={14} />
          <span className="text-[--asfi-purple] font-medium">Preview Issue</span>
          <ChevronRight size={14} />
                  
          <span font-medium>
            {manuscript.title}
          </span>

        </div>
      </div> */}

      {/* 3. Main 3-Column Content Area */}
      <div className="flex-1 flex max-w-[100vw] flex-col lg:flex-row  mx-auto w-full px-6 py-8 gap-4">
        
        {/* COLUMN 1: Sidebar Nav (Left) */}
        <aside className="w-64 hidden lg:block">
          <div className="sticky top-8 space-y-4">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
               <BookOpen size={20} className="text-purple-800" /> Issues
            </h3>
            <nav className="flex flex-col">
              <button className="flex items-center gap-3 px-4 py-3 bg-[--asfi-blue] text-white rounded-lg shadow-md transition-all" onClick={() => document.getElementById('abstract').scrollIntoView({ behavior: 'smooth' })}>
                <FileText size={18} /> Abstract
              </button>
              {/* <a href={manuscript.pdfUrl} className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-800 transition-all border-l-4 border-transparent">
                <Download size={18} /> Full-Text (PDF)
              </a> */}
              <button className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-800 transition-all border-l-4 border-transparent" onClick={() => document.getElementById('full-text').scrollIntoView({ behavior: 'smooth' })}>
                <FileText size={18} /> Full-Text
              </button>
              <button className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-800 transition-all border-l-4 border-transparent" onClick={() => document.getElementById('authors').scrollIntoView({ behavior: 'smooth' })}>
                <Users size={18} /> Authors
              </button>
            </nav>
          </div>
        </aside>

        {/* COLUMN 2: Main Content (Center) */}
        <main className="flex-1 min-w-0">
          <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
            {/* Manuscript Visual Header */}
        <div className="relative bg-gray-900 min-h-64">
  <img 
    src={manuscript.manuscriptPhoto} 
    alt="Banner" 
    className="w-full h-full object-cover opacity-50 absolute inset-0"
  />
  <div className="relative p-3 sm:p-6 md:p-8 flex flex-col justify-end min-h-64">
    {/* Title - fully visible with no cutting */}
    <h2 className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-bold leading-tight mb-3 break-words whitespace-normal">
      {manuscript.title}
    </h2>
    
    {/* Authors */}
    <div className="bg-white/90 p-2 sm:p-3 rounded text-[#80078b] font-bold text-xs sm:text-sm mb-2">
      {manuscript.authorsTop}
    </div>
    
    {/* Your published styles - kept exactly as you had them */}
 <div className="flex items-center justify-between mt-1 bg-[#80078b] px-2 py-0.5 sm:py-1 rounded">

  {/* LEFT: Published date */}
  <div className="flex items-center">
    <span className="text-white text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 font-bold uppercase tracking-wider">
      Published
    </span>
    <span className="text-white/90 text-[10px] sm:text-xs font-medium px-2 py-0.5 sm:py-1">
      {manuscript.publishedDate}
    </span>
  </div>

  {/* RIGHT: Corresponding author */}
  <span className="text-white text-[10px] sm:text-xs font-medium text-right">
    {manuscript.correspondingEmail}
  </span>

</div>
  {/* anchor placeholder for abstract */}
                <div id='abstract'></div>
  </div>

</div>

            <div className="p-6 md:p-10">
              {/* Abstract Section */}
              <section className="mb-12" >
                <h4 className="text-xl font-bold text-gray-800 mb-4 border-l-4 border-purple-800 pl-3">Abstract</h4>
                    {/* Multilingual Support - Directly under Abstract */}
<div className="mt-6 mb-6 pt-4 border-t border-gray-200">
  <div className="flex items-center gap-4">
    <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">Available in:</span>
    <div className="flex gap-1">
      <button 
        onClick={() => switchLanguage('en')}
        className={`px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
          currentLanguage === 'en' 
            ? 'text-[#80078b] border-b-2 border-[#80078b]' 
            : 'text-gray-600 hover:text-[#80078b] border-b-2 border-transparent hover:border-gray-300'
        }`}
      >
        English
      </button>
      <button 
        onClick={() => switchLanguage('fr')}
        className={`px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
          currentLanguage === 'fr' 
            ? 'text-[#80078b] border-b-2 border-[#80078b]' 
            : 'text-gray-600 hover:text-[#80078b] border-b-2 border-transparent hover:border-gray-300'
        }`}
      >
        French
      </button>
      <button 
        onClick={() => switchLanguage('ar')}
        className={`px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
          currentLanguage === 'ar' 
            ? 'text-[#80078b] border-b-2 border-[#80078b]' 
            : 'text-gray-600 hover:text-[#80078b] border-b-2 border-transparent hover:border-gray-300'
        }`}
      >
        Arabic
      </button>
      <button 
        onClick={() => switchLanguage('pt')}
        className={`px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
          currentLanguage === 'pt' 
            ? 'text-[#80078b] border-b-2 border-[#80078b]' 
            : 'text-gray-600 hover:text-[#80078b] border-b-2 border-transparent hover:border-gray-300'
        }`}
      >
        Portuguese
      </button>
    </div>
  </div>
</div>
                
                <RichTextViewer content={getTranslatedAbstract()} />
                
                 {/* anchor placeholder for fulltext*/}
                <div id='full-text'></div>
              </section>

              {/* Full Text Section */}
              <section className="mb-12 pt-8 border-t">
                <h4 className="text-xl font-bold text-gray-800 mb-6 border-l-4 border-purple-800 pl-3">Full Text</h4>
                <RichTextViewer content={manuscript.fullText} />
                {/* anchor placeholder for authors list */}
                <div id='authors'></div>
              </section>

              {/* Authors List Bottom */}
              <section className="bg-gray-50 p-6 rounded-lg mb-8" >
          <br />
                <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Users size={20} className="text-purple-800" /> Authors
                </h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  {manuscript.authorsList.map((author, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                      {author}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t text-sm">
                  <span className="font-bold">Corresponding Author:</span>
                  <a href={`mailto:${manuscript.correspondingEmail}`} className="ml-2 text-purple-700 hover:underline inline-flex items-center gap-1">
                    <Mail size={14} /> {manuscript.correspondingEmail}
                  </a>
                </div>
              </section>

              {/* Bottom Action Button */}
                         {manuscript.pdfUrl && (
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-[#80078b] text-white rounded-xl font-bold hover:bg-[#6a0673] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading ? (
                    <>
                      <Loader size={20} className="animate-spin" />
                      Preparing Download...
                    </>
                  ) : (
                    <>
                      <Download size={20} />
                      Download Full-Text (PDF)
                    </>
                  )}
                </button>
              )}
          
            </div>
          </div>
        </main>

        {/* COLUMN 3: Metadata & Stats (Right) */}
        <aside className="w-full lg:w-72 xl:block space-y-6">
          {/* Issue Info Card */}
          <div className="bg-white p-6 rounded-xl shadow-md border">
            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
              <LinkIcon size={18} className="text-purple-800" /> Issue Information
            </h4>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <Hash size={16} className="mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800">Issue Number</p>
                  <p>{manuscript.issueNumber}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <FileText size={16} className="mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800">Page Number</p>
                  <p>{manuscript.pageNumber}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <LinkIcon size={16} className="mt-1 flex-shrink-0" />
                <div className="break-all">
                  <p className="font-semibold text-gray-800">DOI Number</p>
                  <a href={manuscript.doi} className="text-[--asfi-blue] hover:underline">
                    {manuscript.doi}
                  </a>
                </div>
              </div>
                   <div className="flex items-start gap-2">
                <LinkIcon size={16} className="mt-1 flex-shrink-0" />
                <div className="break-all">
                  <p className="font-semibold text-gray-800">Download PDF</p>
                  <button    onClick={handleDownload}
                  disabled={downloading} className="text-[--asfi-blue] hover:underline">
                    Download PDF
                  </button>
                </div>
              </div>
              {manuscript.doi && (
                <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
                  <FileText size={16} className="mt-1 flex-shrink-0 text-amber-600" />
                  <div className="break-all">
                    <p className="font-semibold text-gray-800">Citations</p>
                    {citationLoading ? (
                      <span className="inline-block mt-1 animate-spin rounded-full h-4 w-4 border-2 border-solid border-amber-500 border-r-transparent"></span>
                    ) : citationCount > 0 ? (
                      <button
                        onClick={() => setShowCitationList(true)}
                        className="text-amber-600 hover:text-amber-700 font-medium hover:underline text-sm"
                      >
                        Cited by {citationCount} {citationCount === 1 ? 'article' : 'articles'}
                      </button>
                    ) : (
                      <span className="text-gray-500 text-sm">No citations found</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dates Card */}
          <div className="bg-gray-100 p-6 rounded-xl border border-gray-200">
             <div className="space-y-3 text-xs uppercase tracking-wider font-bold text-gray-500">
                <div className="flex justify-between">
                  <span>Submitted:</span>
                  <span className="text-gray-800">{manuscript.dateSubmitted}</span>
                </div>
                <div className="flex justify-between">
                  <span>Reviewed:</span>
                  <span className="text-gray-800">{manuscript.dateReviewed}</span>
                </div>
                <div className="flex justify-between">
                  <span>Accepted:</span>
                  <span className="text-gray-800">{manuscript.dateAccepted}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300 text-[--asfi-blue]">
                  <span>Published:</span>
                  <span>{manuscript.publishedDate}</span>
                </div>
             </div>
          </div>

          {/* Stats Box */}
          <div className="bg-[--asfi-blue] p-6 rounded-xl text-white shadow-lg flex flex-col gap-4">
             <div className="flex items-center gap-4 bg-white/10 p-3 rounded-lg">
                <Eye size={24} />
                <div>
                  <p className="text-2xl font-bold">{manuscript.views}</p>
                  <p className="text-[10px] uppercase opacity-70">Total Views</p>
                </div>
             </div>
             <div className="flex items-center gap-4 bg-white/10 p-3 rounded-lg">
                <Download size={24} />
                <div>
                  <p className="text-2xl font-bold">{manuscript.downloads}</p>
                  <p className="text-[10px] uppercase opacity-70">PDF Downloads</p>
                </div>
             </div>
          </div>
        </aside>

      </div>

      {/* Citation List Modal */}
      {showCitationList && manuscript && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                Citing Articles
              </h3>
              <button
                onClick={() => { setShowCitationList(false); }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Article:</p>
                <p className="text-sm text-gray-900 mt-1">{manuscript.title}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Cited by <span className="font-semibold text-amber-600">{citationCount}</span> {citationCount === 1 ? 'article' : 'articles'}
                </p>
              </div>

              {citationList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No citation details available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {citationList.map((citation, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          {citation.citing_doi && (
                            <div className="mb-1">
                              <span className="text-xs font-medium text-gray-500">Citing DOI:</span>
                              <a
                                href={`https://doi.org/${citation.citing_doi}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-1 text-sm text-blue-600 hover:underline break-all"
                              >
                                {citation.citing_doi}
                              </a>
                            </div>
                          )}
                          {citation.publication_date && (
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Date:</span> {citation.publication_date}
                            </div>
                          )}
                          {citation.timespan && (
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Timespan:</span> {citation.timespan}
                            </div>
                          )}
                          {citation.journal_self_citation === 'true' && (
                            <span className="inline-flex items-center px-2 py-0.5 mt-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                              Journal Self-Citation
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => { setShowCitationList(false); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PreviewIssue;